/**
 * A lógica do plano de ação vivo, sem JSX — como todo o resto desta camada.
 *
 * Aqui moram as frases que erram caro num acompanhamento: dizer "3 iniciativas
 * novas" sem nomeá-las manda a pessoa procurar quais; dizer "nada mudou" e
 * esconder o que continua em aberto transforma uma resposta honesta em tela
 * vazia; e inventar novidade onde não houve é o jeito mais rápido de ensinar o
 * leitor a ignorar o produto.
 *
 * A REGRA QUE ATRAVESSA O ARQUIVO: nada aqui recalcula estado. Quem decide se
 * uma tarefa venceu é o servidor (`tarefaStore.estaVencida`), num lugar só —
 * duas definições de "venceu", uma na API e outra na tela, divergem no dia em
 * que uma das duas ganhar uma condição a mais, e o produto passa a pintar de
 * vermelho o que a própria API diz estar em dia.
 */

const DIA_EM_MS = 86400000;

/** 'YYYY-MM-DD' vira data ao MEIO-DIA UTC: fuso não pode fazer o dia andar. */
const comoDia = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(String(iso))) return null;
  return new Date(`${String(iso).slice(0, 10)}T12:00:00Z`);
};

/** 'YYYY-MM-DD HH:MM:SS' (o carimbo do banco, em UTC) vira data. */
const comoInstante = (valor) => {
  if (!valor) return null;
  const texto = String(valor).trim();
  const normalizado = texto.includes('T') ? texto : `${texto.replace(' ', 'T')}Z`;
  const data = new Date(normalizado);
  return Number.isNaN(data.getTime()) ? null : data;
};

const diasEntre = (de, ate) =>
  Math.round((comoDia(ate.toISOString().slice(0, 10)) - de) / DIA_EM_MS);

const dataCurta = (data) =>
  data ? data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : null;

/**
 * O prazo em linguagem de gente.
 *
 * "Vence em 2026-09-30" obriga a pessoa a fazer a conta; "vence em 12 dias" é o
 * que ela precisa saber para decidir se começa hoje.
 */
export const rotuloDoPrazo = (tarefa, hoje = new Date()) => {
  if (!tarefa) return null;

  if (tarefa.estado === 'feita') {
    const quando = dataCurta(comoInstante(tarefa.concluidaEm));
    return { texto: quando ? `Feita em ${quando}` : 'Feita', tom: 'feita' };
  }

  const prazo = comoDia(tarefa.prazoEm);
  if (!prazo) return null;

  const dias = diasEntre(prazo, hoje) * -1;

  if (tarefa.vencida) {
    const atraso = Math.abs(dias);
    if (atraso <= 1) return { texto: 'Venceu ontem', tom: 'vencida' };
    return { texto: `Venceu há ${atraso} dias`, tom: 'vencida' };
  }

  if (tarefa.estado === 'adiada') {
    return { texto: `Adiada para ${dataCurta(prazo)}`, tom: 'adiada' };
  }

  if (dias <= 0) return { texto: 'Vence hoje', tom: 'urgente' };
  if (dias === 1) return { texto: 'Vence amanhã', tom: 'urgente' };
  return { texto: `Vence em ${dias} dias`, tom: dias <= 7 ? 'urgente' : 'normal' };
};

const HORIZONTES = ['h30', 'h60', 'h90'];

const itensDoPlano = (plano) => HORIZONTES.flatMap((h) => (plano?.[h] || []));

/**
 * Quantas iniciativas, quantas resolvidas, quantas atrasadas.
 *
 * `acompanhado` é o que decide se a tela oferece os controles: só o plano que
 * veio decorado pelo servidor tem `chave`, e sem chave não há o que marcar.
 * Visitante anônimo e conta sem mês gravado continuam vendo o plano inteiro,
 * como leitura — o que eles não têm é onde guardar a decisão.
 */
export const resumoDoPlano = (plano) => {
  const itens = itensDoPlano(plano);
  const acompanhado = itens.some((item) => Boolean(item?.chave));

  return {
    acompanhado,
    total: itens.length,
    feitas: itens.filter((i) => i.estado === 'feita').length,
    abertas: itens.filter((i) => i.chave && i.estado !== 'feita').length,
    vencidas: itens.filter((i) => i.vencida).length
  };
};

/** "A", "A e B", "A, B e C" — nunca "3 itens", que manda a pessoa procurar quais. */
const listar = (nomes) => {
  if (!nomes.length) return '';
  if (nomes.length === 1) return nomes[0];
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
};

const plural = (n, singular, pluralizado) => (n === 1 ? singular : pluralizado);

/**
 * "desde ___" — sempre gramatical, em qualquer distância.
 *
 * Um "desde há 5 dias" na primeira dobra do produto é o tipo de erro que faz o
 * leitor duvidar do resto da tela.
 */
const desdeQuando = (desde, hoje) => {
  const data = comoInstante(desde);
  if (!data) return 'a sua última visita';

  const dias = diasEntre(comoDia(data.toISOString().slice(0, 10)), hoje);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias === 2) return 'anteontem';
  return `a sua última visita, em ${dataCurta(data)}`;
};

/**
 * A primeira dobra de quem volta: o que mudou e o que fazer hoje.
 *
 * TRÊS ESTADOS, TODOS PREVISTOS.
 *
 * `primeiraVisita` — quem nunca esteve aqui não recebe um "desde a última
 * visita" vazio; recebe o começo. Fingir um histórico que não existe é a
 * primeira mentira que um produto de acompanhamento pode contar.
 *
 * `semMudanca` — "nada mudou desde ontem" é resposta honesta, e continua sendo
 * motivo para abrir: as iniciativas em aberto seguem lá, e elas aparecem na
 * mesma frase. O que não pode acontecer é a tela inventar novidade para ter o
 * que dizer.
 *
 * `comMudanca` — o que entrou, o que saiu e o que a própria pessoa concluiu,
 * NOMEADOS enquanto couber.
 */
export const quemVolta = (retorno, hoje = new Date()) => {
  if (!retorno) return null;

  const abertas = retorno.totais?.abertas || 0;
  const feitas = retorno.totais?.feitas || 0;
  const vencidas = retorno.totais?.vencidas || 0;
  const base = { abertas, feitas, vencidas, hoje: retorno.hoje || null, mudancas: [] };

  const emAberto = abertas === 0
    ? 'Nenhuma iniciativa em aberto.'
    : `${abertas} ${plural(abertas, 'iniciativa continua', 'iniciativas continuam')} em aberto`
      + (vencidas > 0
        ? `, ${vencidas} ${plural(vencidas, 'delas passou', 'delas passaram')} do prazo.`
        : '.');

  if (retorno.primeiraVisita) {
    return {
      ...base,
      estado: 'primeiraVisita',
      titulo: abertas > 0 ? 'Seu plano de ação está montado' : 'Nenhuma iniciativa em aberto',
      frase: abertas > 0
        ? `${abertas} ${plural(abertas, 'iniciativa saiu', 'iniciativas saíram')} dos seus números. `
          + 'Comece pela primeira e volte para marcar o que já fez.'
        : 'Nenhum sinal do período pede ação agora. Feche o próximo mês para a série continuar.'
    };
  }

  const mudancas = [];
  // O TEXTO DA AÇÃO JÁ TERMINA EM PONTO — e a frase que o cita também.
  // Sem esta poda a tela exibia "Saiu do plano: Reforce ações comerciais e
  // avalie promoções controladas de margem.. O sinal que a gerou parou de
  // disparar." Pontuação dobrada num documento de consultoria é o detalhe que
  // faz o leitor duvidar do resto da página.
  const semPontoFinal = (texto) => String(texto).trim().replace(/[.;,]+$/, '');
  const nomes = (lista) => listar((lista || []).map((t) => semPontoFinal(t.acao)).filter(Boolean));

  if (retorno.novas?.length) {
    const n = retorno.novas.length;
    mudancas.push({
      id: 'novas',
      tipo: 'novas',
      texto: n <= 2
        ? `${plural(n, 'Entrou', 'Entraram')} no plano: ${nomes(retorno.novas)}.`
        : `${n} iniciativas novas entraram no plano.`
    });
  }

  if (retorno.concluidas?.length) {
    const n = retorno.concluidas.length;
    mudancas.push({
      id: 'concluidas',
      tipo: 'concluidas',
      texto: `Você marcou ${n} ${plural(n, 'iniciativa', 'iniciativas')} como ${plural(n, 'feita', 'feitas')}.`
    });
  }

  if (retorno.sumiram?.length) {
    const n = retorno.sumiram.length;
    mudancas.push({
      id: 'sumiram',
      tipo: 'sumiram',
      // NOMEAR O QUE SAIU NÃO É DETALHE. Uma iniciativa que evapora entre duas
      // visitas, sem explicação, faz a pessoa achar que o produto perdeu o
      // trabalho dela — inclusive quando ela mesma já tinha marcado como feita.
      texto: n <= 2
        ? `${plural(n, 'Saiu', 'Saíram')} do plano: ${nomes(retorno.sumiram)}. `
          + `O ${plural(n, 'sinal que a gerou parou', 'sinais que as geraram pararam')} de disparar.`
        : `${n} iniciativas saíram do plano: os sinais que as geraram pararam de disparar.`
    });
  }

  if (!mudancas.length) {
    return {
      ...base,
      estado: 'semMudanca',
      titulo: `Nada mudou desde ${desdeQuando(retorno.desde, hoje)}`,
      frase: emAberto
    };
  }

  return {
    ...base,
    estado: 'comMudanca',
    titulo: `O que mudou desde ${desdeQuando(retorno.desde, hoje)}`,
    frase: emAberto,
    mudancas
  };
};

/**
 * Se esta dobra ganha o topo da tela.
 *
 * A regra mora AQUI, e não nos dois lugares que precisam dela — o cartão, que
 * decide se se desenha, e o dashboard, que decide se mostra este guia ou o do
 * próximo passo do dado. Duas cópias da mesma condição produziriam o estado em
 * que nenhum dos dois aparece, e o topo da tela ficaria simplesmente vazio.
 *
 * Ela é falsa quando não há iniciativa em aberto nem nada a relatar: aí este
 * cartão não tem o que dizer, e o lugar é de quem tem.
 */
export const mereceAPrimeiraDobra = (retorno) => {
  const dobra = quemVolta(retorno);
  if (!dobra) return false;
  return dobra.abertas > 0 || dobra.mudancas.length > 0;
};

export default quemVolta;
